import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { clsx } from "clsx";
import { ChevronDown, ChevronRight, DollarSign, ScrollText } from "lucide-react";
import apiClient from "@/api/client";
import { naturalSortKey } from "@/utils/naturalSort";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useAuthStore } from "@/store/auth";
import { Spinner } from "@/components/ui/Spinner";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { FilterMultiSelect } from "@/components/ui/FilterMultiSelect";
import { XlsxExportButton } from "@/components/ui/XlsxExportButton";
import {
  DEEP_SEARCH_MIN_LEN,
  usePortfolio,
  usePortfolioDeepSearch,
  usePortfolioMilestoneColumn,
  usePortfolioReportColumn,
  useCreateConnection,
  useSuggestedSrid,
  useTaskPolling,
} from "../hooks";
import { downloadPortfolioXlsx, rebuildAllDxfPreviews } from "../api";
import { ParcelExportModal } from "../components/ParcelExportModal";
import {
  countMetaFilters,
  matchesMetaFilters,
  META_EMPTY,
  metaSearchText,
  PortfolioMetadataFilters,
  type MetaFilterDef,
} from "../components/PortfolioMetadataFilters";
import { TopicStatusControl } from "../components/TopicStatusControl";
import { useActiveLeaderships } from "@/features/contractor_led/hooks";
import {
  SheetCalcProvider,
  SheetCalcToggleButton,
  CalcCell,
} from "@/features/finances/components/SheetCalc";
import type {
  DeepSearchRow,
  MilestoneColumnCell,
  PortfolioRow,
  ReportColumnCell,
  ReportColumnParams,
  ReportColumnState,
} from "../types";
import { apiErr, fmtDate, fmtMoney, plProjekty } from "../utils";
import {
  DeadlineBadge,
  milestoneMeta,
  MILESTONE_STATES,
  PageHeader,
  Progress,
  TableShell,
  Th,
  TOPIC_STATUS,
  TOPIC_STATUS_ORDER,
} from "../components/ui";

function CreateConnectionDialog({ onClose }: { onClose: () => void }) {
  const [projects, setProjects] = useState<{ id: string; name: string }[]>([]);
  const [project, setProject] = useState("");
  const [srid, setSrid] = useState(2177);
  // Once the user picks a zone by hand, the suggestion stops overriding it.
  const [sridTouched, setSridTouched] = useState(false);
  const [contractNumber, setContractNumber] = useState("");
  const create = useCreateConnection();
  // Strefa z położenia rysunku przetargu ZE (EPSG:2180 → najbliższy południk PUWG 2000).
  const suggested = useSuggestedSrid(project || undefined);
  const suggestion = suggested.data?.suggestion ?? null;

  useEffect(() => {
    if (suggestion && !sridTouched) setSrid(suggestion.srid);
  }, [suggestion, sridTouched]);

  useEffect(() => {
    apiClient
      .get<{ data: { id: string; name?: string; short_name?: string }[] }>(
        "/projects/",
        { params: { page_size: 1000 } },
      )
      .then((r) => {
        const arr = Array.isArray(r.data.data) ? r.data.data : [];
        setProjects(
          arr
            .map((p) => ({ id: p.id, name: p.name || p.short_name || p.id }))
            .sort((a, b) => naturalSortKey(a.name).localeCompare(naturalSortKey(b.name), "pl")),
        );
      })
      .catch(() => setProjects([]));
  }, []);

  const submit = () => {
    if (!project) return;
    create.mutate(
      {
        project,
        srid,
        contract_number: contractNumber,
      } as never,
      { onSuccess: onClose },
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-md shadow-hard-red"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-2.5 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
          <h3 className="font-mono text-xs font-bold uppercase tracking-widest text-[var(--color-text-primary)]">
            Nowy projekt ZE
          </h3>
        </div>
        <div className="p-4 space-y-3">
          <Field label="Przedsięwzięcie">
            <SearchableSelect
              placeholder="— wybierz —"
              value={project}
              onChange={(e) => setProject(e.target.value)}
              options={projects.map((p) => ({ value: p.id, label: p.name }))}
            />
          </Field>
          <Field label="Strefa SRID">
            <select
              className="input-base"
              value={srid}
              onChange={(e) => {
                setSridTouched(true);
                setSrid(Number(e.target.value));
              }}
            >
              {[2176, 2177, 2178, 2179].map((s) => (
                <option key={s} value={s}>EPSG:{s}</option>
              ))}
            </select>
            {project && suggested.isPending && (
              <p className="mt-1 text-xs text-[var(--color-text-muted)] font-mono">Sprawdzam położenie przetargu…</p>
            )}
            {suggestion && (
              <p
                className="mt-1 text-xs font-mono"
                data-testid="srid-suggestion"
                style={{ color: suggestion.srid === srid ? "var(--color-text-muted)" : "var(--color-primary)" }}
              >
                Sugerowana: EPSG:{suggestion.srid} — {suggestion.source === "drawing" ? "rysunek" : "działki"} przetargu
                „{suggestion.tender_title}” leży na {suggestion.lng.toFixed(2).replace(".", ",")}°E
                {suggestion.variant_labels.length > 0 && ` (wariant ${suggestion.variant_labels.join(", ")})`}.
                {suggestion.crosses_zone_boundary && " Uwaga: trasa przecina granicę stref — przyjęto strefę środka."}
                {suggestion.srid !== srid && " Wybrano inną strefę niż sugerowana."}
              </p>
            )}
            {project && !suggested.isPending && !suggestion && suggested.data?.reason && (
              <p className="mt-1 text-xs text-[var(--color-text-muted)] font-mono">{suggested.data.reason}</p>
            )}
          </Field>
          <Field label="Nr umowy">
            <input className="input-base" value={contractNumber} onChange={(e) => setContractNumber(e.target.value)} />
          </Field>
          <p className="section-label normal-case tracking-normal">
            Termin umowy pochodzi z przedsięwzięcia — nie trzeba go wpisywać.
          </p>
          {create.isError && (
            <p className="text-xs text-[var(--color-primary)] font-mono">
              Nie udało się utworzyć (czy projekt już nie jest projektem ZE?).
            </p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={onClose}>Anuluj</Button>
            <Button size="sm" disabled={!project} loading={create.isPending} onClick={submit}>
              Utwórz
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="section-label">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

// Retroactive sweep: rebuild the raw-DXF QGIS previews of already-uploaded route
// versions (no re-upload). Director/CEO only — mirrors the backend gate.
function RebuildDxfPreviewsDialog({ onClose }: { onClose: () => void }) {
  const [force, setForce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const task = useTaskPolling(taskId);
  const status = task.data?.status;
  const running = !!taskId && (!status || ["pending", "running"].includes(status));

  const start = async () => {
    setError(null);
    try {
      const r = await rebuildAllDxfPreviews(force);
      setTaskId(r.task_id);
    } catch (e) {
      setError(apiErr(e));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-lg shadow-hard-red"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-2.5 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
          <h3 className="font-mono text-xs font-bold uppercase tracking-widest text-[var(--color-text-primary)]">
            Przebuduj podglądy DXF
          </h3>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-sm text-[var(--color-text-secondary)]">
            Buduje ponownie podglądy „Surowy DXF" z już wgranych plików — tylko dla{" "}
            <span className="font-semibold text-[var(--color-text-primary)]">aktualnych</span>{" "}
            wersji tras. Nikt nie musi wrzucać map od nowa; starsze wersje można przebudować
            pojedynczo na mapie projektu.
          </p>
          <label className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)] cursor-pointer select-none">
            <input
              type="checkbox"
              className="accent-[var(--color-primary)]"
              checked={force}
              disabled={!!taskId}
              onChange={(e) => setForce(e.target.checked)}
            />
            Wymuś też przebudowę istniejących podglądów (po zmianie jakości renderu)
          </label>
          {error && <p className="text-xs text-[var(--color-primary)] font-mono">{error}</p>}
          {taskId && task.data && (
            <div className="space-y-1.5">
              <p className="text-xs font-mono text-[var(--color-text-secondary)]">
                {status === "success"
                  ? "Zakończono."
                  : status === "failure"
                    ? "Błąd — szczegóły w logu poniżej."
                    : "W toku… może potrwać dłuższą chwilę."}
              </p>
              <pre className="max-h-56 overflow-y-auto text-[11px] font-mono whitespace-pre-wrap bg-[var(--color-bg-elevated)] border border-[var(--color-border)] p-2">
                {task.data.log || "…"}
              </pre>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            {running && (
              <span className="text-[11px] font-mono text-[var(--color-text-muted)] mr-auto self-center">
                Możesz zamknąć — przebudowa działa dalej w tle.
              </span>
            )}
            <Button variant="ghost" size="sm" onClick={onClose}>
              Zamknij
            </Button>
            {!taskId && (
              <Button size="sm" onClick={start}>
                Rozpocznij
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** „1 tytuł / 2 tytuły / 5 tytułów” — do tooltipów filtru i chipa. */
function plTytuly(n: number): string {
  const last = n % 10;
  const tens = n % 100;
  if (n === 1) return "tytuł";
  if (last >= 2 && last <= 4 && !(tens >= 12 && tens <= 14)) return "tytuły";
  return "tytułów";
}
/** „w 1 projekcie / w 2 projektach”. */
function plProjektach(n: number): string {
  return n === 1 ? "projekcie" : "projektach";
}

// Filtry wielokrotnego wyboru: nic nie zaznaczone = wszystkie, zaznaczone opcje łączy „lub".
const DEADLINE_FILTERS: { value: string; label: string }[] = [
  { value: "overdue", label: "Po terminie" },
  { value: "soon", label: "≤ 14 dni" },
  { value: "approaching", label: "≤ 30 dni" },
  { value: "ok", label: "W terminie" },
  { value: "none", label: "Brak terminu" },
];

const PROGRESS_FILTERS: { value: string; label: string }[] = [
  { value: "not_started", label: "Nierozpoczęte (0%)" },
  { value: "in_progress", label: "W toku" },
  { value: "done", label: "Zakończone (100%)" },
  { value: "problem", label: "Z problemem na działkach" },
];

const TOPIC_FILTERS: { value: string; label: string }[] = TOPIC_STATUS_ORDER.map((s) => ({
  value: s,
  label: TOPIC_STATUS[s].label,
}));

// „Posiada Wody Polskie" — „tak" = kamień wodny ruszony (Dotyczy i dalej), żywe
// zamówienie Wody Polskie albo niewykluczone zdarzenie detekcji (wody_applies_q).
// Wartości = `wody_status` wiersza; „tak" to zaznaczone obie opcje „Tak — …".
const WODY_FILTERS: { value: string; label: string }[] = [
  { value: "none", label: "Nie" },
  { value: "contractor", label: "Tak — podwykonawca" },
  { value: "internal", label: "Tak — robimy my" },
];

const AGREEMENT_FILTERS: { value: string; label: string }[] = [
  { value: "open", label: "Otwarte" },
  { value: "closed", label: "Zamknięte" },
];

/** Czy wiersz pasuje do JEDNEJ opcji filtra „Postęp". */
function matchesProgress(r: PortfolioRow, value: string): boolean {
  const pct = r.progress.parcels_pct;
  switch (value) {
    case "not_started": return pct <= 0;
    case "in_progress": return pct !== 0 && pct < 100;
    case "done": return pct >= 100;
    case "problem": return r.progress.parcels_problem > 0;
    default: return true;
  }
}

// Raporty okresowe do operatora: „" chowa kolumnę, „column" ją pokazuje. Zawężanie
// wierszy to osobne kontrolki obok (przełącznik „tylko z obowiązkiem" + gotowe okno
// terminu) — liczone PO STRONIE SERWERA, więc to samo zapytanie da się powtórzyć w ARON-ie
// czy skrypcie, a komórka dostaje wszystkie terminy z okna, nie tylko najbliższy.
type ReportFilter = "" | "column";
const REPORT_FILTERS: { value: ReportFilter; label: string }[] = [
  { value: "", label: "Raport: bez kolumny" },
  { value: "column", label: "Raport: kolumna" },
];
// Gotowe okna terminu. Dni liczą się od dziś (zaległości wpadają, bo brak dolnej
// granicy); miesiące są KALENDARZOWE — pod koniec miesiąca „do końca miesiąca" to 1–2
// dni, a raport z 3. dnia następnego miesiąca jest realnie pilny, stąd osobne opcje.
type ReportWindow = "" | "overdue" | "7d" | "14d" | "30d" | "current_month" | "next_month";
const REPORT_WINDOWS: { value: ReportWindow; label: string; days?: number }[] = [
  { value: "", label: "Termin raportu: dowolny" },
  { value: "overdue", label: "Zaległy" },
  { value: "7d", label: "≤ 7 dni", days: 7 },
  { value: "14d", label: "≤ 14 dni", days: 14 },
  { value: "30d", label: "≤ 30 dni", days: 30 },
  { value: "current_month", label: "Ten miesiąc" },
  { value: "next_month", label: "Następny miesiąc" },
];
const REPORT_STATE_ORDER: ReportColumnState[] = ["overdue", "due_soon", "planned", "unscheduled", "none"];

/** Dzisiejsza data lokalna + `days` jako ISO (RRRR-MM-DD) — bez przeskoku strefy z toISOString. */
function isoDateFromToday(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** Parametry zapytania report-column dla wybranego zawężenia (pusty obiekt = bez zawężeń). */
function reportColumnParams(required: boolean, window: ReportWindow): ReportColumnParams {
  const params: ReportColumnParams = {};
  if (required) params.required = true;
  const win = REPORT_WINDOWS.find((w) => w.value === window);
  if (window === "overdue") {
    params.status = ["overdue"];
  } else if (win?.days) {
    // Próg „zbliża się" zrównany z oknem, żeby kolor plakietki zgadzał się z filtrem.
    params.due_to = isoDateFromToday(win.days);
    params.within_days = win.days;
  } else if (window === "current_month" || window === "next_month") {
    params.period = window;
  }
  return params;
}

/** Pola paska filtrów mają wspólne h-10 — natywny select ignoruje line-height i bez
 *  stałej wysokości wychodził niższy od pól tekstowych i list wielokrotnego wyboru. */
function FilterSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      className="input-base h-10 w-auto min-w-[150px]"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

/** Pojedynczy wybór z wyszukiwaniem — dziś już tylko kolumna „Kamień" (kilkadziesiąt
 *  kroków). Stała szerokość: pole szerokie na wybraną etykietę przestawiałoby cały pasek. */
function SearchFilterSelect({
  value,
  onChange,
  placeholder,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  /** Etykieta stanu „bez filtra" — pierwsza pozycja listy, wybór czyści filtr. */
  placeholder: string;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="w-full sm:w-56">
      <SearchableSelect
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        options={options}
        // „Klient: wszyscy" to stan filtra, nie podpowiedź — ten sam kolor co natywne selecty obok.
        className="h-10 !text-[var(--color-text-primary)]"
      />
    </div>
  );
}

type SortKey =
  | "project" | "topic" | "branch" | "coordinator" | "deadline" | "parcels" | "milestones"
  | "agreements" | "milestone_cell" | "report_cell" | "value" | "received";

// Komórka „Najbliższy raport": data najbliższego terminu pokolorowana pilnością, a dla
// projektów bez obowiązku — kreska z powodem w dymku (nN / wyłączone / temat zamknięty).
function ReportCell({ cell }: { cell?: ReportColumnCell }) {
  if (!cell) {
    return <span className="font-mono text-[var(--color-text-muted)]">…</span>;
  }
  if (cell.state === "none") {
    return (
      <span className="font-mono text-[var(--color-text-muted)]" title={cell.reason}>
        —
      </span>
    );
  }
  if (cell.state === "unscheduled") {
    return (
      <span title={cell.reason}>
        <Badge color="yellow">brak daty umowy</Badge>
      </span>
    );
  }
  const color = cell.state === "overdue" ? "red" : cell.state === "due_soon" ? "yellow" : "green";
  const days = cell.days_left ?? 0;
  const when =
    days < 0 ? `zaległy od ${-days} dn.` : days === 0 ? "termin dzisiaj" : days === 1 ? "termin jutro" : `za ${days} dn.`;
  // Przy oknie terminu: PRZYSZŁE terminy z okna inne niż najbliższy (np. październik
  // przy zaległym sierpniu) — pokazujemy je obok, żeby było widać, CO wpadło w filtr.
  // Zaległe z okna pomijamy: te streszcza już licznik „+N" przy plakietce.
  const inWindow = (cell.window_slots ?? []).filter(
    (w) => w.due_date !== cell.next_due_date && w.state !== "overdue",
  );
  const title = [
    `Raport nr ${cell.sequence_no ?? "?"} — ${when}`,
    cell.overdue_count > 1 ? `zaległych terminów: ${cell.overdue_count}` : null,
    inWindow.length
      ? `w wybranym oknie: ${inWindow.map((w) => `nr ${w.sequence_no} ${fmtDate(w.due_date)}`).join(", ")}`
      : null,
    `co ${cell.interval_months} mies.`,
    cell.reason,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <span title={title} className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <Badge color={color}>{cell.next_due_date ? fmtDate(cell.next_due_date) : "—"}</Badge>
      {cell.overdue_count > 1 && (
        <span className="font-mono text-xs text-[var(--color-primary)]">+{cell.overdue_count - 1}</span>
      )}
      {inWindow.map((w) => (
        <span key={w.due_date} className="font-mono text-xs text-[var(--color-text-secondary)]">
          → {fmtDate(w.due_date)}
        </span>
      ))}
    </span>
  );
}

// Komórka kolumny „Kamień": stan kroku albo „—", gdy projekt takiego kroku nie ma
// (różne szablony procesu — nie każdy projekt przechodzi wszystkie kamienie).
function MilestoneCell({ cell }: { cell?: MilestoneColumnCell }) {
  if (!cell) {
    return (
      <span className="font-mono text-[var(--color-text-muted)]" title="Ten projekt nie ma tego kamienia">
        —
      </span>
    );
  }
  const meta = milestoneMeta(cell.state);
  const title = [
    cell.label,
    meta.label,
    cell.detail || null,
    cell.completed_at ? `zakończono: ${fmtDate(cell.completed_at)}` : null,
    cell.due_date ? `termin: ${fmtDate(cell.due_date)}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <span title={title}>
      <Badge color={meta.color}>{meta.label}</Badge>
    </span>
  );
}

// Komórka „Otrzymano": kiedy projekt trafił do tego, kto go dziś prowadzi — podwykonawcy
// (przekazanie) albo koordynatora (najwcześniejszy początek obecnych prowadzących).
function ReceivedCell({ received }: { received: PortfolioRow["received"] }) {
  if (!received) {
    return (
      <span
        className="font-mono text-[var(--color-text-muted)]"
        title="Projekt nie ma koordynatora ani nie został przekazany podwykonawcy"
      >
        —
      </span>
    );
  }
  const contractor = received.by === "contractor";
  const title = contractor
    ? `Przekazano podwykonawcy ${received.name} ${fmtDate(received.date)}`
    : received.exact
      ? `Koordynator ${received.name} prowadzi projekt od ${fmtDate(received.date)}`
      : `Koordynator ${received.name} prowadził już projekt ${fmtDate(received.date)}, gdy ruszył `
        + "rejestr przypisań — dokładna data przejęcia nie jest znana";
  return (
    <span title={title} className="flex flex-col whitespace-nowrap">
      <span className="font-mono text-[var(--color-text-secondary)]">
        {received.exact ? "" : "≤ "}
        {fmtDate(received.date)}
      </span>
      <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
        {contractor ? "podwykonawca" : "koordynator"}
      </span>
    </span>
  );
}

interface SortState {
  key: SortKey | null;
  dir: "asc" | "desc";
}

// Persist the portfolio's filters + sort so they survive a page reload.
const FILTERS_STORAGE_KEY = "powerProjects.portfolio.filters";

// Filtry wielokrotnego wyboru — każdy trzyma listę zaznaczonych opcji ([] = wszystkie).
const LIST_FILTER_KEYS = [
  "client", "branch", "coordinator", "ledContractor", "deadline", "topic", "progress", "agreements",
  "wody",
] as const;
type ListFilterKey = (typeof LIST_FILTER_KEYS)[number];

interface StoredFilters extends Record<ListFilterKey, string[]> {
  search: string;
  /** Filtr karty specyfiki (pojedynczy wybór): "" = wszystkie, "only" = tylko projekty pod
   *  szczególną opieką. */
  care: string;
  /** Klucz kroku pokazywanego w dodatkowej kolumnie „Kamień" ("" = kolumny nie ma). */
  milestoneKey: string;
  /** Kolumna „Najbliższy raport" ("" = kolumny nie ma). */
  report: ReportFilter;
  /** Tylko projekty z obowiązkiem raportowania (stan ≠ none). */
  reportRequired: boolean;
  /** Gotowe okno terminu raportu ("" = dowolny). */
  reportWindow: ReportWindow;
  /** Filtry zaawansowane (metadane projektu): klucz filtru → wartość ("" = nieaktywny). */
  meta: Record<string, string>;
  /** Czy panel filtrów zaawansowanych jest rozwinięty. */
  metaOpen: boolean;
  sort: SortState;
}

/** Ostatnio otwarty projekt z tej listy — podświetlamy go po powrocie (tylko jeden). */
const LAST_VISITED_STORAGE_KEY = "powerProjects.portfolio.lastVisited";

function readLastVisited(): string {
  try {
    return localStorage.getItem(LAST_VISITED_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

/** Stary zapis (do 10.2026) trzymał w każdym filtrze listowym JEDNĄ wartość ("" = wszystkie),
 *  a „Wody Polskie" miały zbiorcze „tak"/„nie" — przekładamy, żeby nikt nie stracił filtrów. */
function normalizeListFilters(raw: Record<string, unknown>): Pick<StoredFilters, ListFilterKey> {
  const out = {} as Pick<StoredFilters, ListFilterKey>;
  for (const key of LIST_FILTER_KEYS) {
    const v = raw[key];
    out[key] = Array.isArray(v)
      ? v.filter((x): x is string => typeof x === "string")
      : typeof v === "string" && v ? [v] : [];
  }
  out.wody = out.wody.flatMap((w) =>
    w === "yes" ? ["contractor", "internal"] : w === "no" ? ["none"] : [w],
  );
  return out;
}

function readStoredFilters(): Partial<StoredFilters> {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    const stored = { ...parsed, ...normalizeListFilters(parsed) } as Partial<StoredFilters>;
    // Stary zapis (do 09.2026) trzymał zawężenie w jednym polu `report`
    // („required" / „due_soon" / „overdue") — przekładamy na nowe kontrolki.
    const legacy = stored.report as string | undefined;
    if (legacy === "required") {
      return { ...stored, report: "column", reportRequired: true };
    }
    if (legacy === "due_soon") return { ...stored, report: "column", reportWindow: "14d" };
    if (legacy === "overdue") return { ...stored, report: "column", reportWindow: "overdue" };
    return stored;
  } catch {
    return {};
  }
}

function writeStoredFilters(filters: StoredFilters) {
  try {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // ignore write failures (private mode / quota)
  }
}

function SortHeader({
  label,
  col,
  sort,
  onSort,
  className,
}: {
  label: string;
  col: SortKey;
  sort: SortState;
  onSort: (k: SortKey) => void;
  className?: string;
}) {
  const active = sort.key === col;
  return (
    <Th className={className}>
      <button
        type="button"
        onClick={() => onSort(col)}
        className="inline-flex items-center gap-1 uppercase tracking-widest hover:text-[var(--color-text-primary)] transition-colors"
        title="Sortuj"
      >
        {label}
        <span
          className={
            active
              ? "text-[var(--color-primary)]"
              : "text-[var(--color-text-muted)] opacity-40"
          }
        >
          {active ? (sort.dir === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </button>
    </Th>
  );
}

// Combines the branch + sub-branch into the "Oddział / Rejon" label used
// throughout the portfolio (filter value, sort key, table cell).
const branchLabel = (r: { branch_name: string | null; sub_branch_name: string | null }) =>
  [r.branch_name, r.sub_branch_name].filter(Boolean).join(" / ");

// Kalkulator arkuszowy trzyma w localStorage snapshot zaznaczonych KWOT, a zwykłe
// wylogowanie go nie czyści — stały klucz pokazałby wartości kontraktów następnej
// osobie na tym samym komputerze. Stąd klucz per użytkownik (jak w Fakturach PRZ).
const sheetCalcStorageKey = (userId: string | undefined) =>
  `elg5.zePortfolio.sheetCalc.v1.${userId ?? "anon"}`;

// Trafienia szukania po danych powiązanych, pokazane pod nazwą projektu: bez nich wiersz,
// którego nagłówek nie zawiera frazy, wygląda w wyniku na pomyłkę.
function DeepHitChips({ row }: { row: DeepSearchRow | undefined }) {
  if (!row?.hits.length) return null;
  const rest = row.total - row.hits.length;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      {row.hits.map((h, i) => (
        <span
          key={`${h.kind}-${h.value}-${i}`}
          className="inline-flex max-w-[220px] items-center gap-1 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-1 font-mono text-[10px] text-[var(--color-text-secondary)]"
          title={`${h.label}: ${h.value}${h.detail ? ` — ${h.detail}` : ""}`}
        >
          <span className="uppercase tracking-wider text-[var(--color-text-muted)]">{h.label}</span>
          <span className="truncate">{h.value}</span>
        </span>
      ))}
      {rest > 0 && (
        <span className="font-mono text-[10px] text-[var(--color-text-muted)]" title="Więcej dopasowań w tym projekcie">
          +{rest}
        </span>
      )}
    </div>
  );
}

/** Parametr adresu włączający filtr „Tytuły do potwierdzenia” — plakietka „N tyt.” w menu
 *  prowadzi tu wprost, żeby jednym klikiem zobaczyć, KTÓRE projekty czekają. */
export const PENDING_TITLES_PARAM = "titles";
export const PENDING_TITLES_VALUE = "pending";

export function PowerProjectsPortfolioPage() {
  // Projekty przekazane podwykonawcom (apps.contractor_led) — chip przy nazwie.
  const ledMap = useActiveLeaderships(true).data;
  const user = useAuthStore((s) => s.user);
  // Zgłoszenia tytułów prawnych od firm prowadzących, czekające na nasze potwierdzenie —
  // ta sama liczba, co plakietka „N tyt.” przy pozycji Projekty (nav_badges), rozbita na
  // projekty. Filtr jest tymczasowy (adres, nie localStorage): to lista „do zrobienia”,
  // nie ustawienie widoku — po tygodniu nikt nie ma wracać do pustego portfela.
  const [searchParams, setSearchParams] = useSearchParams();
  // Pasek i filtr tylko dla tych, którzy mogą potwierdzić (dyr. techniczny / CEO / flaga
  // „Potwierdzanie tytułów od firm (ZE)” od CEO) — reszta nie ma z tą listą co zrobić,
  // a adres z ?titles=pending od kogoś innego nie może im ukryć portfela.
  const canDecideTitles = !!user?.has_led_title_confirmation_permission;
  const pendingTitlesOnly =
    canDecideTitles && searchParams.get(PENDING_TITLES_PARAM) === PENDING_TITLES_VALUE;
  const setPendingTitlesOnly = useCallback(
    (on: boolean) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (on) next.set(PENDING_TITLES_PARAM, PENDING_TITLES_VALUE);
          else next.delete(PENDING_TITLES_PARAM);
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  const pendingTitles = useMemo(() => {
    let titles = 0;
    let projects = 0;
    for (const v of Object.values(ledMap ?? {})) {
      if (v.titles_pending > 0) {
        titles += v.titles_pending;
        projects += 1;
      }
    }
    return { titles, projects };
  }, [ledMap]);
  const { data, isLoading } = usePortfolio();
  // Status tematu z portfela — tylko CEO / dyrektor techniczny (lustro IsTechnicalDirector).
  const canEditTopicStatus =
    !!user && (user.is_ceo || (user.role === "director" && user.is_technical_director));
  // Mirrors the backend gate on rebuild-previews (IsDirectorOrCEO).
  const canRebuildPreviews = !!user && (user.is_ceo || user.role === "director");
  // Hydrate filters from the previously persisted selection (once, on mount).
  const stored = useMemo(readStoredFilters, []);
  const [search, setSearch] = useState(stored.search ?? "");
  const [client, setClient] = useState<string[]>(stored.client ?? []);
  const [branch, setBranch] = useState<string[]>(stored.branch ?? []);
  const [coordinator, setCoordinator] = useState<string[]>(stored.coordinator ?? []);
  const [ledContractor, setLedContractor] = useState<string[]>(stored.ledContractor ?? []);
  const [deadline, setDeadline] = useState<string[]>(stored.deadline ?? []);
  const [topic, setTopic] = useState<string[]>(stored.topic ?? []);
  const [progress, setProgress] = useState<string[]>(stored.progress ?? []);
  const [agreements, setAgreements] = useState<string[]>(stored.agreements ?? []);
  const [wody, setWody] = useState<string[]>(stored.wody ?? []);
  const [care, setCare] = useState(typeof stored.care === "string" ? stored.care : "");
  const [milestoneKey, setMilestoneKey] = useState(stored.milestoneKey ?? "");
  const [report, setReport] = useState<ReportFilter>(stored.report ?? "");
  const [reportRequired, setReportRequired] = useState(stored.reportRequired ?? false);
  const [reportWindow, setReportWindow] = useState<ReportWindow>(stored.reportWindow ?? "");
  const [meta, setMeta] = useState<Record<string, string>>(stored.meta ?? {});
  const [metaOpen, setMetaOpen] = useState(stored.metaOpen ?? false);
  const [sort, setSort] = useState<SortState>(stored.sort ?? { key: null, dir: "asc" });
  const [dialog, setDialog] = useState(false);
  // Kolumna „Wartość" startuje SCHOWANA przy każdym wejściu (celowo nie zapamiętujemy
  // jej w localStorage, tak jak kwota na karcie projektu chowa się pod „•••") —
  // portfel bywa pokazywany na spotkaniach i rzutnikach.
  const [showAmounts, setShowAmounts] = useState(false);
  const [rebuildDialog, setRebuildDialog] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  // Ostatnio kliknięty projekt — trzymany też w localStorage, żeby przetrwał
  // powrót z karty projektu (przeglądarka odmontowuje tę stronę).
  const [lastVisited, setLastVisited] = useState(readLastVisited);

  const markVisited = useCallback((projectId: string) => {
    setLastVisited(projectId);
    try {
      localStorage.setItem(LAST_VISITED_STORAGE_KEY, projectId);
    } catch {
      // ignore write failures (private mode / quota)
    }
  }, []);

  // Persist filters + sort whenever they change, so a reload restores them.
  useEffect(() => {
    writeStoredFilters({
      search, client, branch, coordinator, ledContractor, deadline, topic, progress, agreements, wody, care,
      milestoneKey, report, reportRequired, reportWindow, meta, metaOpen, sort,
    });
  }, [
    search, client, branch, coordinator, ledContractor, deadline, topic, progress, agreements, wody, care,
    milestoneKey, report, reportRequired, reportWindow, meta, metaOpen, sort,
  ]);

  // Szukanie po danych POWIĄZANYCH z projektem (działki, KW, WNU/WPW, zamówienia, pliki,
  // kamienie, warstwy DXF). Wiersz portfela niesie tylko nagłówek projektu, więc te
  // dopasowania liczy serwer — na żądanie i dopiero po odczekaniu na koniec pisania.
  const deepQuery = useDebouncedValue(search.trim(), 350);
  const deepSearch = usePortfolioDeepSearch(deepQuery);
  // Trafienia bierzemy WYŁĄCZNIE wtedy, gdy odpowiedź dotyczy tego, co jest w polu —
  // inaczej po skasowaniu frazy wiersze z poprzedniego szukania zostałyby doklejone.
  const deepHits = useMemo<Record<string, DeepSearchRow>>(
    () =>
      deepSearch.data && deepSearch.data.query === search.trim().toLowerCase()
        ? deepSearch.data.rows
        : {},
    [deepSearch.data, search],
  );
  // Ile projektów wchodzi do wyniku wyłącznie z danych powiązanych — do podsumowania
  // pod polem („nazwa nie pasuje, ale projekt ma taką działkę").
  const deepPending =
    search.trim().length >= DEEP_SEARCH_MIN_LEN
    && (deepSearch.isFetching || deepQuery !== search.trim());

  // Katalog kamieni (zawsze) + stany wybranego kroku (dopiero po wyborze).
  const milestoneColumn = usePortfolioMilestoneColumn(milestoneKey);
  const milestoneOptions = milestoneColumn.data?.options ?? [];
  const milestoneCells = useMemo(
    () =>
      milestoneKey && milestoneColumn.data?.key === milestoneKey
        ? milestoneColumn.data.rows
        : ({} as Record<string, MilestoneColumnCell>),
    [milestoneKey, milestoneColumn.data],
  );
  const milestoneLabel =
    milestoneOptions.find((o) => o.key === milestoneKey)?.label ?? "Kamień";

  // Terminy raportów do operatora — jedno zapytanie na cały portfel, dopiero po
  // włączeniu kolumny. Dopóki odpowiedź nie dotrze, filtr po raportach NIE tnie
  // wierszy (inaczej lista migałaby „pusto" przy każdym wejściu).
  const reportParams = useMemo(
    () => reportColumnParams(reportRequired, reportWindow),
    [reportRequired, reportWindow],
  );
  const reportNarrowed = reportRequired || reportWindow !== "";
  const reportColumn = usePortfolioReportColumn(report !== "", reportParams);
  const reportCells = useMemo(
    () => (report ? reportColumn.data?.rows : undefined) ?? ({} as Record<string, ReportColumnCell>),
    [report, reportColumn.data],
  );
  // Liczniki z `summary` (liczone przed zawężeniem — nie zerują się po wyborze).
  const reportSummary = reportColumn.data?.summary;
  const reportRequiredCount = reportSummary
    ? REPORT_STATE_ORDER.filter((st) => st !== "none").reduce((acc, st) => acc + (reportSummary[st] ?? 0), 0)
    : null;
  const reportWindowOptions = REPORT_WINDOWS.map((w) =>
    w.value === "overdue" && reportSummary
      ? { value: w.value, label: `${w.label} (${reportSummary.overdue ?? 0})` }
      : { value: w.value, label: w.label },
  );

  const onSort = (key: SortKey) =>
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" },
    );

  // A row passes when it matches every active filter. `skip` lets a facet leave
  // out its OWN filter so its option list reflects what the *other* filters
  // allow — picking one filter (e.g. Oddział) narrows the others' choices.
  // `skip.meta` does the same for ONE advanced (metadata) filter.
  const passesFilters = useCallback(
    (
      r: PortfolioRow,
      skip: {
        client?: boolean; branch?: boolean; coordinator?: boolean; ledContractor?: boolean; meta?: string;
      } = {},
    ) => {
      const q = search.trim().toLowerCase();
      if (q) {
        const hay = [
          r.project_name,
          r.client_name,
          r.contract_number,
          r.coordinator_name,
          // Firma prowadząca — ta sama nazwa, co chip „Podwykonawca: …" pod nazwą projektu.
          ledMap?.[r.id]?.contractor_name,
          r.branch_name,
          r.sub_branch_name,
          r.contract_deadline,
          // Uproszczone szukanie po metadanych: te same pola co filtry zaawansowane.
          metaSearchText(r),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        // Nagłówek projektu dopasowuje przeglądarka, resztę (działki, KW, WNU/WPW,
        // zamówienia, pliki, kamienie, warstwy DXF) — serwer.
        if (!hay.includes(q) && !deepHits[r.id]) return false;
      }
      // Każdy filtr listowy: nic nie zaznaczone = bez filtra, zaznaczone opcje łączy „lub".
      const outside = (selected: string[], value: string | null | undefined) =>
        selected.length > 0 && !selected.includes(value ?? "");
      if (!skip.client && outside(client, r.client_name)) return false;
      if (!skip.branch && outside(branch, branchLabel(r))) return false;
      if (!skip.coordinator && outside(coordinator, r.coordinator_name)) return false;
      // Dopóki mapa prowadzeń nie dotarła, nie tniemy (lista nie ma migać „pusto”).
      if (!skip.ledContractor && ledMap && outside(ledContractor, ledMap[r.id]?.contractor_name))
        return false;
      if (outside(deadline, r.deadline_status)) return false;
      if (outside(topic, r.topic_status)) return false;
      if (progress.length && !progress.some((p) => matchesProgress(r, p))) return false;
      if (outside(agreements, r.progress.open_agreements > 0 ? "open" : "closed")) return false;
      if (outside(wody, r.wody_status ?? "none")) return false;
      if (care === "only" && !(r.care_flags?.length)) return false;
      // Dopóki mapa prowadzeń nie dotarła, nie tniemy (lista nie ma migać „pusto”).
      if (pendingTitlesOnly && ledMap && !(ledMap[r.id]?.titles_pending > 0)) return false;
      // Zawężenie po raportach liczy serwer — wiersz spoza zawężenia nie ma komórki.
      // Dopóki odpowiedź nie dotrze, nie tniemy (lista nie ma migać „pusto").
      if (reportNarrowed && reportColumn.data && !reportCells[r.id]) return false;
      if (!matchesMetaFilters(r, meta, skip.meta)) return false;
      return true;
    },
    [
      search, client, branch, coordinator, ledContractor, deadline, topic, progress, agreements, wody, care,
      meta, reportNarrowed, reportColumn.data, reportCells, deepHits, pendingTitlesOnly, ledMap,
    ],
  );

  // Dependent option lists: each dropdown offers only values still reachable
  // under the other active filters. The current selection is always kept in its
  // own list so it stays visible (and clearable) even if it now yields no rows.
  const clientOptions = useMemo(() => {
    const set = new Set<string>();
    (data ?? []).forEach((r) => {
      if (r.client_name && passesFilters(r, { client: true })) set.add(r.client_name);
    });
    client.forEach((v) => set.add(v));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "pl"));
  }, [data, passesFilters, client]);

  const branchOptions = useMemo(() => {
    const set = new Set<string>();
    (data ?? []).forEach((r) => {
      const v = branchLabel(r);
      if (v && passesFilters(r, { branch: true })) set.add(v);
    });
    branch.forEach((v) => set.add(v));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "pl"));
  }, [data, passesFilters, branch]);

  const coordinatorOptions = useMemo(() => {
    const set = new Set<string>();
    (data ?? []).forEach((r) => {
      if (r.coordinator_name && passesFilters(r, { coordinator: true })) set.add(r.coordinator_name);
    });
    coordinator.forEach((v) => set.add(v));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "pl"));
  }, [data, passesFilters, coordinator]);

  const ledContractorOptions = useMemo(() => {
    const set = new Set<string>();
    (data ?? []).forEach((r) => {
      const name = ledMap?.[r.id]?.contractor_name;
      if (name && passesFilters(r, { ledContractor: true })) set.add(name);
    });
    ledContractor.forEach((v) => set.add(v));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "pl"));
  }, [data, ledMap, passesFilters, ledContractor]);

  // Lista wyboru filtru zaawansowanego: wartości wciąż osiągalne pod POZOSTAŁYMI
  // filtrami ("" = są wiersze z pustym polem). Aktualny wybór zostaje na liście,
  // żeby dało się go zobaczyć i wyczyścić, nawet gdy nic już nie zwraca.
  const metaOptionsFor = useCallback(
    (def: MetaFilterDef) => {
      const set = new Set<string>();
      (data ?? []).forEach((r) => {
        if (!passesFilters(r, { meta: def.key })) return;
        const v = def.value(r);
        set.add(v == null ? "" : String(v));
      });
      const selected = meta[def.key];
      if (selected === META_EMPTY) set.add("");
      else if (selected) set.add(selected);
      return Array.from(set);
    },
    [data, passesFilters, meta],
  );

  const setMetaField = useCallback((key: string, value: string) => {
    setMeta((s) => {
      const next = { ...s };
      if (value) next[key] = value;
      else delete next[key];
      return next;
    });
  }, []);

  const metaCount = countMetaFilters(meta);

  const rows = useMemo(
    () => (data ?? []).filter((r) => passesFilters(r)),
    [data, passesFilters],
  );

  // Przycisk „Pokaż kwoty" istnieje tylko dla tych, którym backend w ogóle przysyła
  // jakąkolwiek wartość (CEO, księgowość, dyrektor/koordynator z grantem do kwot
  // przedsięwzięcia). Reszta nie dostaje pustej kolumny obiecującej dane, których
  // i tak nie zobaczy.
  const canSeeAmounts = useMemo(
    () => (data ?? []).some((r) => r.can_view_project_value),
    [data],
  );
  const amountsVisible = showAmounts && canSeeAmounts;

  const sortValue = (r: PortfolioRow, key: SortKey): string | number => {
    switch (key) {
      case "project": return naturalSortKey(r.project_name ?? "").toLowerCase();
      case "topic": return TOPIC_STATUS_ORDER.indexOf(r.topic_status);
      case "branch": return naturalSortKey(branchLabel(r)).toLowerCase();
      case "coordinator": return r.coordinator_name?.toLowerCase() ?? "";
      case "deadline": return r.contract_deadline ?? "";
      case "parcels": return r.progress.parcels_pct;
      case "milestones":
        return r.progress.milestones_total
          ? r.progress.milestones_done / r.progress.milestones_total
          : -1;
      case "agreements": return r.progress.open_agreements;
      // Brak kwoty (nie wpisana albo bez uprawnienia) sortuje się przed najmniejszą.
      case "value": return r.project_value != null ? Number(r.project_value) : -1;
      case "received": return r.received?.date ?? "";
      case "milestone_cell": {
        const cell = milestoneCells[r.id];
        // Brak kroku w projekcie sortuje się przed „Nie rozpoczęto".
        return cell ? MILESTONE_STATES.indexOf(cell.state) : -1;
      }
      case "report_cell": {
        const cell = reportCells[r.id];
        // Najpierw pilność (zaległy → zbliża się → …), w obrębie stanu po dacie terminu;
        // projekty bez obowiązku lądują na końcu.
        if (!cell) return "";
        return `${REPORT_STATE_ORDER.indexOf(cell.state)}:${cell.next_due_date ?? "9999-12-31"}`;
      }
    }
  };

  const sorted = useMemo(() => {
    if (!sort.key) return rows;
    const key = sort.key;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = sortValue(a, key);
      const vb = sortValue(b, key);
      // Empty text values always sort to the bottom, regardless of direction.
      if (va === "" && vb !== "") return 1;
      if (vb === "" && va !== "") return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb), "pl") * dir;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sortValue czyta milestoneCells i reportCells
  }, [rows, sort, milestoneCells, reportCells]);

  const total = (data ?? []).length;
  // Ile WIDOCZNYCH wierszy zawdzięcza obecność danym powiązanym (a nie nagłówkowi).
  const deepMatchCount = useMemo(
    () => rows.filter((r) => deepHits[r.id]).length,
    [rows, deepHits],
  );
  const listFiltersActive = [
    client, branch, coordinator, ledContractor, deadline, topic, progress, agreements, wody,
  ].some((v) => v.length > 0);
  const hasFilters = !!(
    search || care || reportNarrowed || listFiltersActive || metaCount || pendingTitlesOnly
  );
  const clearAll = () => {
    setPendingTitlesOnly(false);
    setSearch("");
    setClient([]);
    setBranch([]);
    setCoordinator([]);
    setLedContractor([]);
    setDeadline([]);
    setTopic([]);
    setProgress([]);
    setAgreements([]);
    setWody([]);
    setCare("");
    // Sama kolumna raportów zostaje — czyścimy tylko zawężenie wierszy.
    setReportRequired(false);
    setReportWindow("");
    setMeta({});
  };

  return (
    <SheetCalcProvider storageKey={sheetCalcStorageKey(user?.id)}>
    <div className="space-y-4">
      <PageHeader
        title="Projekty ZE"
        subtitle="Portfel — nadzór postępów · DLC_01"
        actions={
          <div className="flex items-center gap-2">
            {canSeeAmounts && (
              <Button
                size="sm"
                variant={amountsVisible ? "secondary" : "ghost"}
                onClick={() => setShowAmounts((v) => !v)}
              >
                <DollarSign size={14} />
                {amountsVisible ? "Ukryj kwoty" : "Pokaż kwoty"}
              </Button>
            )}
            {amountsVisible && <SheetCalcToggleButton size="sm" tone="excel" />}
            {canRebuildPreviews && (
              <Button size="sm" variant="ghost" onClick={() => setRebuildDialog(true)}>
                Przebuduj podglądy DXF
              </Button>
            )}
            {/* Portfel filtruje i sortuje w przeglądarce — do pliku idą dokładnie
                widoczne wiersze, w bieżącej kolejności. Kolumna „Wartość" tylko przy
                włączonym „Pokaż kwoty". */}
            <XlsxExportButton
              label="Pobierz projekty (.xlsx)"
              title="Pobierz odfiltrowane projekty (w bieżącej kolejności) jako arkusz Excel"
              count={sorted.length}
              disabled={isLoading}
              download={() => downloadPortfolioXlsx(sorted.map((r) => r.id), amountsVisible)}
            />
            <Button size="sm" variant="secondary" onClick={() => setExportOpen(true)}>
              Pobierz listę działek
            </Button>
            <Button size="sm" onClick={() => setDialog(true)}>+ Nowy projekt</Button>
          </div>
        }
      />

      {/* Tytuły prawne zgłoszone przez firmy prowadzące, czekające na NASZE potwierdzenie —
          ta sama liczba, co plakietka „N tyt.” w menu. Osobny pasek NAD filtrami, bo to
          zadanie do zrobienia, nie kolejny wymiar filtrowania. Widać go, gdy jest co
          potwierdzać albo gdy zawężenie już działa (żeby dało się je zdjąć po zejściu do zera). */}
      {canDecideTitles && (pendingTitles.titles > 0 || pendingTitlesOnly) && (
        <div
          role="region"
          aria-label="Tytuły prawne do potwierdzenia"
          className={clsx(
            "flex flex-wrap items-center gap-x-3 gap-y-2 border px-3 py-2 text-sm",
            pendingTitlesOnly
              ? "border-amber-500 bg-amber-100 dark:border-amber-600 dark:bg-amber-900/50"
              : "border-amber-400 bg-amber-50 dark:border-amber-700/70 dark:bg-amber-950/30",
          )}
        >
          <ScrollText size={16} className="shrink-0 text-amber-700 dark:text-amber-300" />
          <span className="font-mono text-[0.7rem] font-bold uppercase tracking-widest text-amber-800 dark:text-amber-300">
            Tytuły prawne do potwierdzenia
          </span>
          <span className="inline-flex min-w-[1.5rem] items-center justify-center bg-amber-600 px-1.5 font-mono text-xs font-bold leading-5 text-white dark:bg-amber-500">
            {pendingTitles.titles}
          </span>
          <span className="text-amber-950 dark:text-amber-100">
            {pendingTitles.titles > 0
              ? `${pendingTitles.titles} ${plTytuly(pendingTitles.titles)} od firm prowadzących czeka na potwierdzenie `
                + `w ${pendingTitles.projects} ${plProjektach(pendingTitles.projects)}.`
              : "Nic już nie czeka na potwierdzenie."}
            {pendingTitlesOnly && " Lista poniżej pokazuje tylko te projekty."}
          </span>
          <Button
            size="sm"
            variant={pendingTitlesOnly ? "secondary" : "warning"}
            className="ml-auto"
            aria-pressed={pendingTitlesOnly}
            onClick={() => setPendingTitlesOnly(!pendingTitlesOnly)}
          >
            {pendingTitlesOnly ? "Pokaż wszystkie projekty" : "Pokaż tylko te projekty"}
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input
          placeholder="Szukaj: nazwa, umowa, oddział, podwykonawca, EPSG, działka, KW, WNU/WPW, zamówienie, plik, kamień, warstwa DXF…"
          title={
            "Poza nagłówkiem projektu (nazwa, klient, nr umowy, prowadzący, podwykonawca "
            + "prowadzący projekt, oddział, EPSG, "
            + "projektant, metadane) szuka też w danych z zakładek karty projektu: numery "
            + "działek i obręby, numery KW, numery wniosków WNU i pakietów WPW, numery "
            + "zamówień (podwykonawców, wypisów EGiB, mapy zasadniczej, MPZP), nazwy plików, "
            + "nazwy kamieni i nazwy warstw wgranego DXF-a."
          }
          className="input-base h-10 w-full sm:w-auto sm:flex-1 sm:min-w-[220px] sm:max-w-md"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <FilterMultiSelect
          variant="field"
          label="Klient"
          options={clientOptions.map((c) => ({ value: c, label: c }))}
          selected={client}
          onChange={setClient}
          searchable
        />
        <FilterMultiSelect
          variant="field"
          label="Oddział / Rejon"
          options={branchOptions.map((b) => ({ value: b, label: b }))}
          selected={branch}
          onChange={setBranch}
          searchable
        />
        <FilterMultiSelect
          variant="field"
          label="Prowadzący"
          options={coordinatorOptions.map((c) => ({ value: c, label: c }))}
          selected={coordinator}
          onChange={setCoordinator}
          searchable
        />
        {/* Projekty przekazane firmom (chip „Podwykonawca: …" pod nazwą projektu). */}
        <FilterMultiSelect
          variant="field"
          label="Podwykonawca"
          options={ledContractorOptions.map((c) => ({ value: c, label: c }))}
          selected={ledContractor}
          onChange={setLedContractor}
          searchable
        />
        <FilterMultiSelect
          variant="field"
          label="Status"
          options={TOPIC_FILTERS}
          selected={topic}
          onChange={setTopic}
        />
        <FilterMultiSelect
          variant="field"
          label="Termin"
          options={DEADLINE_FILTERS}
          selected={deadline}
          onChange={setDeadline}
        />
        <FilterMultiSelect
          variant="field"
          label="Postęp"
          options={PROGRESS_FILTERS}
          selected={progress}
          onChange={setProgress}
        />
        <FilterMultiSelect
          variant="field"
          label="Uzgodnienia"
          options={AGREEMENT_FILTERS}
          selected={agreements}
          onChange={setAgreements}
        />
        <FilterMultiSelect
          variant="field"
          label="Wody Polskie"
          options={WODY_FILTERS}
          selected={wody}
          onChange={setWody}
        />
        {/* Karta specyfiki: zawęź portfel do projektów „trudnych" oddziałów. */}
        <FilterSelect
          value={care}
          onChange={setCare}
          options={[
            { value: "", label: "Opieka: wszystkie" },
            { value: "only", label: "⚠ Szczególna opieka" },
          ]}
        />
        {/* Nie filtruje wierszy — dokłada kolumnę ze stanem WYBRANEGO kamienia. */}
        <SearchFilterSelect
          value={milestoneKey}
          onChange={setMilestoneKey}
          placeholder="Kamień: bez kolumny"
          options={milestoneOptions.map((o) => ({
            value: o.key,
            label: `${o.label} (${o.count})`,
          }))}
        />
        {/* Kolumna „Najbliższy raport"; po włączeniu — zawężenie do projektów z obowiązkiem
            raportowania i gotowe okna terminu (liczone po stronie serwera). */}
        <FilterSelect
          value={report}
          onChange={(v) => setReport(v as ReportFilter)}
          options={REPORT_FILTERS}
        />
        {report && (
          <>
            <label className="flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)] cursor-pointer select-none whitespace-nowrap">
              <input
                type="checkbox"
                className="accent-[var(--color-primary)]"
                checked={reportRequired}
                onChange={(e) => setReportRequired(e.target.checked)}
              />
              Tylko z obowiązkiem raportowania
              {reportRequiredCount != null ? ` (${reportRequiredCount})` : ""}
            </label>
            <FilterSelect
              value={reportWindow}
              onChange={(v) => setReportWindow(v as ReportWindow)}
              options={reportWindowOptions}
            />
          </>
        )}
        {/* Filtry po metadanych projektu — chowane, bo jest ich kilkadziesiąt. */}
        <Button
          variant={metaCount > 0 ? "secondary" : "ghost"}
          size="sm"
          onClick={() => setMetaOpen((v) => !v)}
        >
          <span className="inline-flex items-center gap-1">
            {metaOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            Zaawansowane{metaCount > 0 ? ` (${metaCount})` : ""}
          </span>
        </Button>
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAll}
            className="!border-[var(--color-primary)] !text-[var(--color-primary)] hover:bg-[var(--color-primary-dim)]"
          >
            Wyczyść filtry
          </Button>
        )}
        <span className="section-label ml-auto whitespace-nowrap">
          {rows.length} / {total}
        </span>
      </div>

      {/* Co dołożyło szukanie po danych powiązanych — bez tego wiersz, którego nazwa
          nie pasuje do frazy, wygląda w wyniku na pomyłkę. */}
      {search.trim().length > 0 && (
        <div className="-mt-2 mb-3 text-xs text-[var(--color-text-muted)]">
          {search.trim().length < DEEP_SEARCH_MIN_LEN ? (
            <>Dopasowanie po działkach, KW, wnioskach i plikach — od {DEEP_SEARCH_MIN_LEN} znaków.</>
          ) : deepPending ? (
            <>Szukam w działkach, KW, wnioskach, zamówieniach, plikach, kamieniach i warstwach DXF…</>
          ) : deepMatchCount > 0 ? (
            <>
              Dopasowano po danych powiązanych: <strong>{plProjekty(deepMatchCount)}</strong>
              {deepSearch.data?.truncated && " (wynik obcięty — zawęź frazę)"}
            </>
          ) : (
            <>Brak dopasowań w działkach, KW, wnioskach, zamówieniach, plikach, kamieniach i warstwach DXF.</>
          )}
        </div>
      )}

      {metaOpen && (
        <PortfolioMetadataFilters
          values={meta}
          onChange={setMetaField}
          onClear={() => setMeta({})}
          optionsFor={metaOptionsFor}
        />
      )}

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : (
        <TableShell>
          <thead>
            <tr>
              <SortHeader label="Projekt" col="project" sort={sort} onSort={onSort} />
              <SortHeader label="Status" col="topic" sort={sort} onSort={onSort} />
              <SortHeader label="Oddział / Rejon" col="branch" sort={sort} onSort={onSort} />
              <SortHeader label="Prowadzący" col="coordinator" sort={sort} onSort={onSort} />
              <SortHeader label="Termin" col="deadline" sort={sort} onSort={onSort} />
              <SortHeader label="Działki" col="parcels" sort={sort} onSort={onSort} className="min-w-[150px]" />
              <SortHeader label="Kamienie" col="milestones" sort={sort} onSort={onSort} />
              {milestoneKey && (
                <SortHeader
                  label={milestoneLabel}
                  col="milestone_cell"
                  sort={sort}
                  onSort={onSort}
                  className="min-w-[130px]"
                />
              )}
              {report && (
                <SortHeader
                  label="Najbliższy raport"
                  col="report_cell"
                  sort={sort}
                  onSort={onSort}
                  className="min-w-[130px]"
                />
              )}
              <SortHeader label="Uzgodnienia" col="agreements" sort={sort} onSort={onSort} />
              {amountsVisible && (
                <SortHeader
                  label="Wartość"
                  col="value"
                  sort={sort}
                  onSort={onSort}
                  className="text-right"
                />
              )}
              <SortHeader label="Otrzymano" col="received" sort={sort} onSort={onSort} />
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, rowIdx) => {
              const visited = !!lastVisited && r.project_id === lastVisited;
              return (
              <tr
                key={r.id}
                className={`border-t border-[var(--color-border-muted)] hover:bg-[var(--color-bg-elevated)] transition-colors${
                  visited ? " bg-[var(--color-bg-elevated)]" : ""
                }`}
              >
                <td
                  className={`px-3 py-2.5 max-w-[360px]${
                    visited ? " shadow-[inset_3px_0_0_var(--color-primary)]" : ""
                  }`}
                >
                  <Link
                    className={`transition-colors hover:text-[var(--color-primary)] ${
                      visited
                        ? "font-semibold text-[var(--color-primary)]"
                        : "font-medium text-[var(--color-text-primary)]"
                    }`}
                    to={`/power-projects/${r.project_id}`}
                    title={visited ? "Ostatnio otwarty projekt" : undefined}
                    // Zwykły klik / ctrl+klik oraz środkowy przycisk (nowa karta).
                    onClick={() => markVisited(r.project_id)}
                    onAuxClick={(e) => {
                      if (e.button === 1) markVisited(r.project_id);
                    }}
                  >
                    {r.project_name}
                  </Link>
                  {ledMap?.[r.id] && (
                    <Link
                      to={`/power-projects/led/${ledMap[r.id].leadership_id}`}
                      className="ml-1.5 inline-block border border-[var(--color-primary)] px-1 font-mono text-[10px] uppercase tracking-wider text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-white"
                      title={`Prowadzi ${ledMap[r.id].contractor_name} — otwórz widok nadzorczy`}
                    >
                      Podwykonawca: {ledMap[r.id].contractor_name}
                    </Link>
                  )}
                  {canDecideTitles && (ledMap?.[r.id]?.titles_pending ?? 0) > 0 && (
                    <Link
                      to={`/power-projects/led/${ledMap![r.id].leadership_id}`}
                      className="ml-1 inline-block border border-amber-500 bg-amber-50 px-1 font-mono text-[10px] uppercase tracking-wider text-amber-900 hover:bg-amber-500 hover:text-white dark:border-amber-600 dark:bg-amber-950/40 dark:text-amber-200"
                      title={`${ledMap![r.id].titles_pending} ${plTytuly(ledMap![r.id].titles_pending)} do potwierdzenia — otwórz widok nadzorczy i potwierdź`}
                    >
                      {ledMap![r.id].titles_pending} tyt.
                    </Link>
                  )}
                  {(r.care_flags?.length ?? 0) > 0 && (
                    <span
                      className="ml-1.5 text-amber-600 dark:text-amber-400 cursor-help"
                      title={`Szczególna opieka: ${r.care_flags!.map((f) => f.label).join(" · ")}`}
                    >
                      ⚠
                    </span>
                  )}
                  {/* Dlaczego ten projekt jest w wyniku, skoro jego nazwa nie pasuje. */}
                  <DeepHitChips row={deepHits[r.id]} />
                </td>
                <td className="px-3 py-2.5">
                  {/* Edycja statusu wprost z portfela — tylko CEO / dyrektor techniczny. */}
                  <TopicStatusControl
                    connectionId={r.id}
                    status={r.topic_status}
                    canEdit={canEditTopicStatus}
                    contractStatus={r.project_contract_status}
                    contractTerminatedOn={r.project_contract_terminated_on}
                    projectId={r.project_id}
                  />
                </td>
                <td className="px-3 py-2.5 text-[var(--color-text-secondary)]">
                  {branchLabel(r) || "—"}
                </td>
                <td className="px-3 py-2.5 text-[var(--color-text-secondary)]">{r.coordinator_name || "—"}</td>
                <td className="px-3 py-2.5"><DeadlineBadge status={r.deadline_status} date={r.contract_deadline} /></td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <Progress pct={r.progress.parcels_pct} className="w-20" />
                    <span
                      className="font-mono text-[var(--color-text-secondary)]"
                      title="uzgodnione / wykorzystywane (w nawiasie wszystkie, razem z zamówionymi „na zapas”)"
                    >
                      {r.progress.parcels_ok_used}/{r.progress.parcels_used}
                      {r.progress.parcels_total !== r.progress.parcels_used && (
                        <span className="text-[var(--color-text-muted)]"> ({r.progress.parcels_total})</span>
                      )}
                    </span>
                    {r.progress.parcels_problem > 0 && (
                      <span className="font-mono text-[var(--color-primary)]">⚠{r.progress.parcels_problem}</span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2.5 font-mono text-[var(--color-text-secondary)]">
                  {r.progress.milestones_done}/{r.progress.milestones_total}
                </td>
                {milestoneKey && (
                  <td className="px-3 py-2.5">
                    <MilestoneCell cell={milestoneCells[r.id]} />
                  </td>
                )}
                {report && (
                  <td className="px-3 py-2.5">
                    <ReportCell cell={reportCells[r.id]} />
                  </td>
                )}
                <td className="px-3 py-2.5 font-mono">
                  {r.progress.open_agreements > 0 ? (
                    <span className="text-amber-600">{r.progress.open_agreements} otwarte</span>
                  ) : (
                    <span className="text-emerald-600">✓</span>
                  )}
                </td>
                {amountsVisible && (
                  <td className="px-3 py-2.5 text-right font-mono whitespace-nowrap text-[var(--color-text-secondary)]">
                    {r.project_value != null ? (
                      // Kwota jest komórką kalkulatora — klik/przeciągnięcie w trybie
                      // „Licz jako arkusz" wrzuca ją do sumy na dolnym pasku.
                      <CalcCell
                        id={`ze:${r.id}:0`}
                        tableId="ze-portfolio"
                        index={rowIdx}
                        col={0}
                        value={Number(Number(r.project_value).toFixed(2))}
                        currency="PLN"
                      >
                        {fmtMoney(r.project_value)}
                      </CalcCell>
                    ) : (
                      <span
                        className="text-[var(--color-text-muted)]"
                        title={
                          r.can_view_project_value
                            ? "Wartość nie została wpisana na przedsięwzięciu"
                            : "Brak dostępu do kwot tego przedsięwzięcia"
                        }
                      >
                        —
                      </span>
                    )}
                  </td>
                )}
                <td className="px-3 py-2.5">
                  <ReceivedCell received={r.received} />
                </td>
              </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={9 + (milestoneKey ? 1 : 0) + (report ? 1 : 0) + (amountsVisible ? 1 : 0)}
                  className="px-3 py-10 text-center section-label tracking-wide"
                >
                  {total === 0 ? "Brak projektów ZE." : "Brak wyników dla wybranych filtrów."}
                </td>
              </tr>
            )}
          </tbody>
        </TableShell>
      )}

      {dialog && <CreateConnectionDialog onClose={() => setDialog(false)} />}
      {rebuildDialog && <RebuildDxfPreviewsDialog onClose={() => setRebuildDialog(false)} />}
      {exportOpen && <ParcelExportModal onClose={() => setExportOpen(false)} />}
    </div>
    </SheetCalcProvider>
  );
}
